import { useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Recipe } from '../types/recipes';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { trackRecipeView } from '@/lib/analytics';
import { useRecipeCouponsUI } from '../hooks/use-recipe-coupons-ui';
import { RecipeCouponBadge } from './RecipeCouponBadge';
import { RecipeCouponsSheet } from './RecipeCouponsSheet';
import { IconChefHat, IconClock, IconTemperature, IconToolsKitchen2, IconToolsKitchen3, IconUsersGroup, IconWorld } from '@tabler/icons-react';

interface RecipeDetailSheetProps {
  recipe: Recipe | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RecipeDetailSheet = ({ recipe, open, onOpenChange }: RecipeDetailSheetProps) => {
  const {
    matchingCoupons,
    hasMatches,
    couponsSheetOpen,
    setCouponsSheetOpen,
    handleBadgeClick,
  } = useRecipeCouponsUI(recipe);

  // Track recipe view when sheet opens
  useEffect(() => {
    if (open && recipe) {
      trackRecipeView({
        recipeId: recipe.id,
        recipeName: recipe.RecipeTitle,
        recipeCuisine: recipe.recipe_cuisine?.RecipeCuisine,
        recipeType: recipe.recipe_type?.RecipeType,
      });
    }
  }, [open, recipe]);

  if (!recipe) return null;

  const imageUrl = recipe.RecipeImage?.url || '';
  const imageAlt = recipe.RecipeImage?.alternativeText || recipe.RecipeTitle;

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[90vw] sm:max-w-2xl p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b text-left">
          <SheetTitle className="text-2xl">{recipe.RecipeTitle}</SheetTitle>
          {recipe.RecipeDescription && (
            <SheetDescription className="text-base pt-2">
              {recipe.RecipeDescription}
            </SheetDescription>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Recipe Image */}
          {imageUrl && (
            <div className="rounded-2xl overflow-hidden">
              <AspectRatio ratio={16 / 9}>
                <img
                  src={imageUrl}
                  alt={imageAlt}
                  className="w-full h-full object-cover"
                />
              </AspectRatio>
            </div>
          )}

          {/* Recipe Meta Info */}
          <div className="flex flex-wrap items-center gap-3">
            {hasMatches && (
              <RecipeCouponBadge count={matchingCoupons.length} onClick={handleBadgeClick} />
            )}
            {recipe.recipe_cuisine && (
              <Badge variant="secondary" className="text-sm bg-emerald-500 text-white">
                <IconWorld className="h-3 w-3 mr-1" />
                {recipe.recipe_cuisine.RecipeCuisine}
              </Badge>
            )}
            {recipe.recipe_type && (
              <Badge variant="default" className="text-sm bg-indigo-500 text-white">
                <IconToolsKitchen3 className="h-3 w-3 mr-1" />
                {recipe.recipe_type.RecipeType}
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-0 text-sm">
            {recipe.RecipeTotalTime && (
              <div className="flex items-center gap-3 sm:pr-2 sm:border-r sm:border-border">
                <IconClock className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Total Time</span>
                  <span className="font-medium">{recipe.RecipeTotalTime}</span>
                </div>
              </div>
            )}
            {recipe.RecipeTotalPrepTime && (
              <div className="flex items-center gap-3 sm:px-2 sm:border-r sm:border-border">
                <IconChefHat className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Prep Time</span>
                  <span className="font-medium">{recipe.RecipeTotalPrepTime}</span>
                </div>
              </div>
            )}
            {recipe.RecipeTotalCookTime && (
              <div className="flex items-center gap-3 sm:px-2 sm:border-r sm:border-border">
                <IconTemperature className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Cook Time</span>
                  <span className="font-medium">{recipe.RecipeTotalCookTime}</span>
                </div>
              </div>
            )}
            {recipe.RecipeServingSize && (
              <div className="flex items-center gap-3 sm:pl-2">
                <IconUsersGroup className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">Servings</span>
                  <span className="font-medium">{recipe.RecipeServingSize}</span>
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Ingredients */}
          {recipe.RecipeIngredients && recipe.RecipeIngredients.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                {/* <IconToolsKitchen2 className="h-5 w-5" /> */}
                Ingredients
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {recipe.RecipeIngredients.map((ingredient) => (
                  <li key={ingredient.id} className="flex items-start gap-2">
                    {/* <span className="text-primary mt-1">•</span> */}
                    <span className="flex-1">
                      <span className="font-medium capitalize">{ingredient.RecipeIngredient}</span>
                      {ingredient.RecipeServingSize && (
                        <div className="text-muted-foreground lowercase">
                           {ingredient.RecipeServingSize}
                        </div>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Separator />

          {/* Steps */}
          {recipe.RecipeSteps && recipe.RecipeSteps.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold mb-4">Instructions</h3>
              <ol className="space-y-3">
                {recipe.RecipeSteps.map((step, index) => (
                  <li key={step.id} className="bg-muted/50 rounded-lg p-4 flex items-start gap-4">
                    <span className="flex-shrink-0 text-3xl font-bold text-muted-foreground">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="flex-1 pt-1 text-foreground sentence-case">{step.RecipeStep}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>

    {couponsSheetOpen && (
      <RecipeCouponsSheet
        open={couponsSheetOpen}
        onOpenChange={setCouponsSheetOpen}
        recipe={recipe}
        matchingCoupons={matchingCoupons}
      />
    )}
    </>
  );
};